/**
 * A VIDEO'S CHECKLIST (v1.0.29): the article checklist's rows over `getStructuralRules?kind=video`
 * (no word floor, no picture rule - its picture is the video), and two rows of its own around them:
 * the video is ready, and all eight rules of the video guidelines are confirmed. The cover row is
 * the structural one, measured as the server measures a video's cover - in either orientation, the
 * longer side against the width floor and the shorter against the height floor - so a 9:16 cover
 * passes exactly when the same picture turned 16:9 would (`videoStructuralRows`).
 *
 * A live stream (1.2.0) confirms no rules: its own row in their place says its planned times are
 * saved and its start is still ahead (`streamTimesRow`).
 */
import { VIDEO_RULES } from '../../features/cabinet/api/editor-api';
import type {
  ArticleVideo,
  StructuralRules,
  VideoRule,
  WorkingStream,
} from '../../features/cabinet/api/editor-api';
import { formatInstant } from '../../features/cabinet/format';
import type { ArticlesT } from '../articles/articles-i18n';
import { checklistFor } from '../articles/editor/document';
import type { ChecklistRow, DraftBase, EditorDraft } from '../articles/editor/document';
import type { VideoT } from './video-i18n';

export function videoRow(video: ArticleVideo | null, t: VideoT): ChecklistRow {
  const ready = video?.state === 'ready';
  return {
    rule: 'video_ready',
    label: t('checklist.video'),
    state: ready ? 'ok' : 'failing',
    actual: null,
    min: null,
    max: null,
    detail:
      video === null
        ? t('checklist.videoNone')
        : video.state === 'failed'
          ? t('checklist.videoFailed')
          : ready
            ? t('checklist.videoReady')
            : t('checklist.videoPreparing'),
  };
}

export function rightsRow(confirmed: ReadonlySet<VideoRule>, t: VideoT): ChecklistRow {
  const done = VIDEO_RULES.filter((rule) => confirmed.has(rule)).length;
  return {
    rule: 'video_rights',
    label: t('checklist.rights'),
    state: done === VIDEO_RULES.length ? 'ok' : 'failing',
    actual: done,
    min: VIDEO_RULES.length,
    max: null,
    detail: t('checklist.rightsCount', { done: String(done), total: String(VIDEO_RULES.length) }),
  };
}

/**
 * A live stream's planned times as the platform holds them - both saved, and the start after `now`,
 * the platform's own instant (the app reads no clock), as the submit asks. With `now` not known the
 * start is not judged here; the submit still is.
 */
export function streamTimesRow(
  stream: WorkingStream,
  now: string | undefined,
  t: VideoT,
): ChecklistRow {
  const { startsAt, endsAt } = stream;
  const set = startsAt !== null && endsAt !== null;
  const passed = set && now !== undefined && Date.parse(startsAt) <= Date.parse(now);
  return {
    rule: 'stream_times',
    label: t('checklist.streamTimes'),
    state: set && !passed ? 'ok' : 'failing',
    actual: null,
    min: null,
    max: null,
    detail: !set
      ? t('checklist.streamTimesNone')
      : passed
        ? t('checklist.streamTimesPassed')
        : t('checklist.streamTimesSet', { when: formatInstant(startsAt) }),
  };
}

/**
 * The structural rows. The cover floor is the policy's width by height for a cover wider than it is
 * tall, and turned for a taller one - so the row compares, and says, the floor the server applies
 * to this cover. With no cover yet, the floor is said in the video's own shape.
 */
export function videoStructuralRows(
  draft: EditorDraft,
  base: DraftBase,
  rules: StructuralRules,
  videoVertical: boolean,
  t: ArticlesT,
): readonly ChecklistRow[] {
  const width = base.coverWidthPx;
  const height = base.coverHeightPx;
  const turn =
    draft.coverFileId === null
      ? videoVertical
      : width !== null && height !== null && height > width;
  if (!turn) {
    return checklistFor(draft, base, rules, t, 'video');
  }
  const turned: StructuralRules = {
    ...rules,
    rules: rules.rules.map((rule) =>
      rule.rule === 'cover_dimensions'
        ? { ...rule, minWidthPx: rule.minHeightPx ?? null, minHeightPx: rule.minWidthPx ?? null }
        : rule,
    ),
  };
  return checklistFor(draft, base, turned, t, 'video');
}
